import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, flow } from 'lodash-es';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  VehicleModel,
  VehicleEntities,
  buildMakeModelDefaultYear,
  buildMmyModelFamilyIdsPath,
  buildModelFamilyIdsModelYearsPath,
} from 'client/data/models/vehicle-v2';
import { PUB_STATES } from 'client/constants/pub-states';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { getFilteredModelYears, isUsedModelYear } from 'site-modules/shared/utils/core-page/model-year-helpers';
import { venomHistory } from 'client/utils/history/venom-history';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { SpeculationRule } from 'site-modules/shared/components/speculation-rule/speculation-rule';

function YearDropdownUI({ modelYears = null, defaultYear = null, value = '', descriptionId = null }) {
  const [hasSpeculation, enableSpeculation] = useToggle(false, { limit: 1 });

  const selectYear = useCallback(({ url }) => {
    venomHistory.push(url);
  }, []);

  if (isEmpty(modelYears)) {
    return null;
  }

  const years = modelYears.map(({ id, publicationStates, year, makeSlug, modelSlug }) => {
    const isUsed = isUsedModelYear(publicationStates);

    let label;
    switch (true) {
      case isUsed: {
        label = `${year} - Used`;
        break;
      }
      case publicationStates.includes(PUB_STATES.PRE_PROD): {
        label = `${year} - Upcoming`;
        break;
      }
      default: {
        label = `${year} - New`;
      }
    }

    return {
      id: `${id}`,
      label,
      url: getCoreUrl({ makeSlug, modelSlug, year, defaultYear }, isUsed),
      withSpeculation: !isUsed,
    };
  });
  const speculationUrls = years
    .filter(({ id, withSpeculation }) => withSpeculation && id !== value)
    .map(({ url }) => url);

  return (
    <Fragment>
      <StyledSelect
        value={value}
        onChange={selectYear}
        onClick={enableSpeculation}
        name="select-year"
        valueKey="id"
        labelText="Select year"
        wrapperClassName="label-inside me-1 mb-1_5 w-100"
        labelClassName="small mb-0_5"
        overrideClasses="year-select d-block w-100 px-1 pt-1_75 pb-0_5 size-16 bg-white text-black rounded-8"
        options={years}
        data-tracking-id="year_select"
        aria-describedby={descriptionId}
      />
      {hasSpeculation && <SpeculationRule urls={speculationUrls} />}
    </Fragment>
  );
}

YearDropdownUI.propTypes = {
  modelYears: PropTypes.arrayOf(VehicleEntities.ModelYearByFamilyId),
  defaultYear: PropTypes.number,
  value: PropTypes.string,
  descriptionId: PropTypes.string,
};

export const YearDropdown = flow(
  component =>
    connectToModel(component, {
      defaultYear: bindToPath(
        ({ params }) => buildMakeModelDefaultYear({ make: params.make, model: params.model }),
        VehicleModel
      ),
      modelYears: bindToPath(
        ({ modelFamilyIds }) => !isEmpty(modelFamilyIds) && buildModelFamilyIdsModelYearsPath(modelFamilyIds),
        VehicleModel,
        (modelYears, { modelName }) => getFilteredModelYears(modelYears, modelName)
      ),
    }),
  component =>
    connectToModel(component, {
      modelFamilyIds: bindToPath(
        ({ params }) => buildMmyModelFamilyIdsPath({ make: params.make, model: params.model, year: params.year }),
        VehicleModel
      ),
    })
)(YearDropdownUI);

export const YearDropdownGraphql = connectToModel(YearDropdownUI, {
  defaultYear: bindToPath(
    ({ params }) => buildMakeModelDefaultYear({ make: params.make, model: params.model }),
    VehicleModel
  ),
  modelYears: bindToPath(
    ({ modelFamilyIds }) => buildModelFamilyIdsModelYearsPath(modelFamilyIds),
    VehicleModel,
    (modelYears, { modelName }) => getFilteredModelYears(modelYears, modelName)
  ),
});
